import Big from "big.js";
import { addDays } from "../days";
import type { ValidationIssue } from "../index";
import type {
  AttributesStatus,
  AttributeTaskStage,
  AttributeTaskStatus,
  BrandType,
  IntercompanyPriceMethod,
  LinesheetStatus,
  PriceChannel,
  PriceStatus,
} from "./enums";

// Rules of the catalog the apps apply too (pattern.md §4): the browser shows only what the domain would accept, with
// the same code. The domain still enforces them (workers/catalog, linesheet.rules.ts).

/**
 * Which status each linesheet action moves from and to (50-api/catalog.md §1): the brand submits a draft, Tomorrow
 * approves it or sends it back, and only an approved linesheet is reopened. One table per action, so an action
 * cannot borrow another's move (returning and reopening both lead to draft).
 */
export const linesheetTransitions = {
  submit: { draft: ["pending"], pending: [], approved: [] },
  return: { draft: [], pending: ["draft"], approved: [] },
  approve: { draft: [], pending: ["approved"], approved: [] },
  reopen: { draft: [], pending: [], approved: ["pending", "draft"] },
} as const satisfies Record<string, Record<LinesheetStatus, readonly LinesheetStatus[]>>;

export type LinesheetAction = keyof typeof linesheetTransitions;

/** Whether `action` can start from `status`. */
export function canMove(action: LinesheetAction, status: LinesheetStatus): boolean {
  return linesheetTransitions[action][status].length > 0;
}

/**
 * The brand changes its linesheet in `draft`, Tomorrow in `draft` and `pending`; nobody changes an archived one.
 * The sync flags alone are Tomorrow's in every status: they say where the approved linesheet goes (FR-M2-07). Not
 * while a sync is running, which reads them.
 */
export function canEditLinesheet(
  actor: { isInternal: boolean },
  ls: { status: LinesheetStatus; archived: boolean; syncing?: boolean },
  { flagsOnly = false }: { flagsOnly?: boolean } = {},
): boolean {
  if (ls.archived) return false;
  if (flagsOnly && actor.isInternal) return !ls.syncing;
  return ls.status === "draft" || (actor.isInternal && ls.status === "pending");
}

/** A combination of gender, category and subcategory the systems downstream know (`reference.taxonomy_rule`). */
export interface TaxonomyRule {
  gender: string;
  productCategory: string;
  /** Null: the category has no subcategories for that gender. */
  productSubcategory: string | null;
}

type Taxonomy = { [K in keyof TaxonomyRule]: string | null };

/**
 * v0.2 §4 rule 5: only the combinations valid downstream are offered and accepted. A product still without gender or
 * category is not judged (incomplete lines are saved); without a subcategory, gender and category must go together.
 */
export function isValidCombination(rules: readonly TaxonomyRule[], p: Taxonomy): boolean {
  if (p.gender === null || p.productCategory === null) return true;
  return rules.some(
    (r) =>
      r.gender === p.gender &&
      r.productCategory === p.productCategory &&
      (p.productSubcategory === null || r.productSubcategory === p.productSubcategory),
  );
}

/** The categories a form offers for a gender, all of them until a gender is chosen. */
export function categoriesOf(rules: readonly TaxonomyRule[], gender: string | null): Set<string> {
  return new Set(rules.filter((r) => gender === null || r.gender === gender).map((r) => r.productCategory));
}

/** The subcategories a form offers for a gender and a category, each narrowing the list only once it is chosen. */
export function subcategoriesOf(
  rules: readonly TaxonomyRule[],
  gender: string | null,
  productCategory: string | null,
): Set<string> {
  return new Set(
    rules
      .filter((r) => gender === null || r.gender === gender)
      .filter((r) => productCategory === null || r.productCategory === productCategory)
      .flatMap((r) => r.productSubcategory ?? []),
  );
}

/** What FR-M2-04 looks at on a line: the commercial fields Business Central needs. */
export interface LineSyncFacts extends Taxonomy {
  styleCode: string | null;
  colourDescription: string | null;
  sizeRun: string | null;
  /** `isValidCombination` of gender, category and subcategory. */
  validCombination: boolean;
  sizeCount: number;
  /** At least one price list with the brand's wholesale price. */
  hasBrandPrice: boolean;
  /**
   * The SKUs of the line still without a code, with Tomorrow's codes they would be made of (ADR-188): given only
   * to who writes those codes, Tomorrow; a brand does not see what is not its to complete.
   */
  uncodedSkus?: number;
  itemCode?: string | null;
  tomorrowColourCode?: string | null;
}

/**
 * FR-M2-04 and v0.2 §4 rule 4: a line goes to the sync with style, colour, gender, category, subcategory and size
 * run, in a combination valid downstream, at least one size and the brand's price. Import and export attributes are
 * not needed here (FR-M2-05). A SKU without a code needs Tomorrow's style and colour codes, with no fallback
 * (ADR-188). The issues carry the fields of the line form.
 */
export function lineSyncIssues(line: LineSyncFacts): ValidationIssue[] {
  const required = (field: string, value: string | null | undefined): ValidationIssue[] =>
    value?.trim() ? [] : [{ field, rule: "required" }];
  const coded = !line.uncodedSkus;
  return [
    ...required("styleCode", line.styleCode),
    ...required("colourDescription", line.colourDescription),
    ...required("gender", line.gender),
    ...required("productCategory", line.productCategory),
    ...required("productSubcategory", line.productSubcategory),
    ...(line.validCombination ? [] : [{ field: combinationField(line), rule: "invalid_combination" }]),
    ...required("sizeRun", line.sizeRun),
    ...(line.sizeCount > 0 ? [] : [{ field: "sizes", rule: "required" }]),
    ...(line.hasBrandPrice ? [] : [{ field: "brandPrices", rule: "required" }]),
    ...(coded ? [] : required("itemCode", line.itemCode)),
    ...(coded ? [] : required("tomorrowColourCode", line.tomorrowColourCode)),
  ];
}

/** The field an invalid combination is shown on: the most specific one the product has. */
export const combinationField = (p: Pick<Taxonomy, "productSubcategory">) =>
  p.productSubcategory === null ? "productCategory" : "productSubcategory";

/**
 * How lines leave a linesheet (50-api/catalog.md §1): Tomorrow removes them while the linesheet is editable; the
 * brand removes them until its first submission, and from then on asks for the deletion, which Tomorrow decides.
 */
export function lineRemoval(
  actor: { isInternal: boolean },
  ls: { status: LinesheetStatus; archived: boolean; firstSubmitted: boolean },
): "remove" | "request" | "none" {
  if (!canEditLinesheet(actor, ls)) return "none";
  return actor.isInternal || !ls.firstSubmitted ? "remove" : "request";
}

/**
 * Tomorrow's prices are worked on between the approval of the linesheet and the approval of the prices
 * (50-api/catalog.md §3.2): calculation and overrides need the linesheet approved and the prices still pending.
 */
export function canEditPrices(ls: { status: LinesheetStatus; priceStatus: PriceStatus; archived: boolean }): boolean {
  return !ls.archived && ls.status === "approved" && ls.priceStatus === "pending";
}

// --- SKU attributes (M4, 50-api/catalog.md §4) ---

/**
 * The day a brand is asked to submit the linesheet of a campaign by: `deadlineDays` before the campaign starts
 * (ADR-076). Days as `YYYY-MM-DD`, counted on the UTC calendar. Shared with who writes the email of the invitation,
 * which states the same day the list shows.
 */
export const invitationDeadline = (startDate: string, deadlineDays: number): string =>
  addDays(startDate, -deadlineDays);

/**
 * Which attributes status each action moves from and to: the brand (or Tomorrow for it) submits a draft, Tomorrow
 * takes it back, or sends it to Business Central (FR-M4-06). The outcome of that sync, not an action, takes
 * `sync_in_progress` to `approved`, or back to `pending`.
 */
export const attributesTransitions = {
  submit: { draft: ["pending"], pending: [], sync_in_progress: [], approved: [] },
  reopen: { draft: [], pending: ["draft"], sync_in_progress: [], approved: ["pending", "draft"] },
  sync: { draft: [], pending: ["sync_in_progress"], sync_in_progress: [], approved: [] },
} as const satisfies Record<string, Record<AttributesStatus, readonly AttributesStatus[]>>;

export type AttributesAction = keyof typeof attributesTransitions;

/** Whether `action` can start from `status`. */
export function canMoveAttributes(action: AttributesAction, status: AttributesStatus): boolean {
  return attributesTransitions[action][status].length > 0;
}

/**
 * Attributes are filled in while they are a draft, and by Tomorrow also once submitted; never during the sync, after
 * it, or on an archived linesheet. Which SKUs the brand may touch is a matter of locks (FR-M4-02).
 */
export function canEditAttributes(
  actor: { isInternal: boolean },
  task: { attributesStatus: AttributesStatus; archived: boolean },
): boolean {
  if (task.archived) return false;
  return task.attributesStatus === "draft" || (actor.isInternal && task.attributesStatus === "pending");
}

/** What the required-attribute rules look at on an SKU. Decimals as text. */
export interface AttributeFacts {
  /** No category has attributes of its own yet (Q-CAT-01): the matrix will read it. */
  productCategory: string | null;
  ean: string | null;
  hsCode: string | null;
  commodityCode: string | null;
  manufacturerId: string | null;
  compositionExtras: string | null;
  washingCareInstructions: string | null;
  weightGross: string | null;
  weightNet: string | null;
  weightUnit: string | null;
  countryWhenceConsigned: string | null;
  animalScientificName: string | null;
  animalMaterialPct: string | null;
  animalCountryOfOrigin: string | null;
  farmRaised: boolean | null;
  dyed: boolean | null;
}

export type AttributeField = Exclude<keyof AttributeFacts, "productCategory" | "farmRaised" | "dyed">;

const text = (field: AttributeField) => (sku: Partial<AttributeFacts>) => Boolean(sku[field]?.trim());
/** A weight of zero is a weight nobody entered. */
const positive = (field: "weightGross" | "weightNet") => (sku: Partial<AttributeFacts>) => Number(sku[field] ?? 0) > 0;

const filled: Record<AttributeField, (sku: Partial<AttributeFacts>) => boolean> = {
  ean: text("ean"),
  hsCode: text("hsCode"),
  commodityCode: text("commodityCode"),
  manufacturerId: text("manufacturerId"),
  compositionExtras: text("compositionExtras"),
  washingCareInstructions: text("washingCareInstructions"),
  weightGross: positive("weightGross"),
  weightNet: positive("weightNet"),
  weightUnit: text("weightUnit"),
  countryWhenceConsigned: text("countryWhenceConsigned"),
  animalScientificName: text("animalScientificName"),
  animalMaterialPct: text("animalMaterialPct"),
  animalCountryOfOrigin: text("animalCountryOfOrigin"),
};

/**
 * Required on every SKU: what the current portal asks at the confirmation, plus the codes FR-M4-07 blocks on, and the
 * animal data on every SKU as the current portal asks them (ADR-178 §2.1); farm raised and dyed stay optional.
 */
const alwaysRequired = [
  "ean",
  "hsCode",
  "commodityCode",
  "manufacturerId",
  "compositionExtras",
  "washingCareInstructions",
  "weightGross",
  "weightNet",
  "weightUnit",
  "countryWhenceConsigned",
  "animalScientificName",
  "animalMaterialPct",
  "animalCountryOfOrigin",
] as const satisfies readonly AttributeField[];

const missingOf = <F extends AttributeField>(sku: Partial<AttributeFacts>, fields: readonly F[]): F[] =>
  fields.filter((f) => !filled[f](sku));

/**
 * The required attributes an SKU still misses (FR-M4-01, FR-M4-05), by DTO field. The same for every product category
 * until Operations gives the matrix (Q-CAT-01).
 */
export function missingAttributes(sku: AttributeFacts): AttributeField[] {
  return missingOf(sku, alwaysRequired);
}

const forInbound = ["ean", "commodityCode", "manufacturerId"] as const satisfies readonly AttributeField[];

/**
 * FR-M4-07: without these the inbound DDT of the SKU is not created. Also applied by procurement, in the three-way check
 * of an inbound, to the attributes it reads via SQL (ADR-131).
 */
export function missingForInbound(
  sku: Pick<AttributeFacts, (typeof forInbound)[number]>,
): (typeof forInbound)[number][] {
  return missingOf(sku, forInbound);
}

/** FR-M4-07: without the HS code the export documents of the SKU are not generated. */
export function missingForExport(sku: AttributeFacts): AttributeField[] {
  return missingOf(sku, ["hsCode"]);
}

// --- Price calculation (FR-M3-03): the formulas of the current portal (data-model README §7.1, ADR-053), in exact
// decimals. The app previews a line with the same code; the domain is the one that writes the prices. ---

/** What the Price Setup says of a price list. Percentages in percentage points, as decimal text. */
export interface PriceListSetup {
  priceList: string;
  channel: PriceChannel;
  region: string | null;
  distributionDiscountPct: string | null;
  tomorrowMarginPct: string | null;
  retailMultiplier: string | null;
  extraMarginPct: string | null;
  freightInPct: string;
  dutiesInPct: string;
  warehousingPct: string;
  freightOutPct: string;
  dutiesOutPct: string;
  /** The currencies the list sells in, in order, each with its extra margin on the retail price. */
  currencies: { currency: string; extraMarginPct: string }[];
}

export interface PricingSetup {
  brandType: BrandType;
  /** The price lists to calculate, in order: the base one (EXW-IT) first. */
  lists: PriceListSetup[];
  fxRates: { from: string; to: string; rate: string }[];
  /** `pricing.wholesale_roundup`: wholesale prices rounded up to the unit. Retail prices always are. */
  wholesaleRoundup: boolean;
  /** `pricing.invested_retail_roundup`: retail prices of an invested brand rounded up to the 1 or to the 10. */
  investedRetailRoundup: 1 | 10;
}

/** Level 1 of FR-M3-03: what the brand entered for a price list and currency. */
export interface BrandPrice {
  priceList: string;
  currency: string;
  brandPurchasePrice: string | null;
  brandWholesalePrice: string | null;
  brandRetailPrice: string | null;
}

/** Tomorrow's prices for a price list and currency; null where the brand's prices do not allow a calculation. */
export interface CalculatedPrice {
  priceList: string;
  currency: string;
  purchasePrice: string | null;
  wholesalePrice: string | null;
  retailPrice: string | null;
}

/** A sample: the brand's wholesale price that stands for "no price", kept symbolic through every level. */
const SAMPLE_PRICE = "0.01";
/** `numeric(18,4)` of the price columns. */
const MONEY_SCALE = 4;
/** The DDP lists of the euro area start from the Italian EXW list, as in the current portal. */
const exwRegionOf = (region: string | null) => (region === "EUR" ? "IT" : region);

interface Amounts {
  purchase: Big | null;
  wholesale: Big;
  retail: Big | null;
}

/** The amounts of a price list and currency at full precision, and whether they come from a sample. */
interface Calculated extends Amounts {
  sample: boolean;
}

/** A calculated combination the others can start from. */
interface Working extends Calculated {
  list: PriceListSetup;
  currency: string;
}

/**
 * Tomorrow's prices of a line for every price list and currency of the setup, from the prices the brand entered.
 * EXW lists first, because the DDP ones start from them; a list without brand prices starts from the base price,
 * the first one the brand entered in the order of the setup, converted with Finance's rates.
 */
export function calculateLinePrices(setup: PricingSetup, brandPrices: readonly BrandPrice[]): CalculatedPrice[] {
  const own = new Map(brandPrices.map((p) => [comboKey(p.priceList, p.currency), p]));
  const combos = setup.lists.flatMap((list) => list.currencies.map((c) => ({ list, currency: c.currency })));
  const done = new Map<string, Working>();
  const channelOf = (list: PriceListSetup): PriceChannel => (setup.brandType === "agency" ? "agency" : list.channel);

  const base = combos
    .map((c) => own.get(comboKey(c.list.priceList, c.currency)))
    .find((p) => p !== undefined && isBase(setup.brandType, p));

  for (const { list, currency } of combos) {
    if (channelOf(list) !== "exw") continue;
    const brand = own.get(comboKey(list.priceList, currency));
    const amounts =
      setup.brandType === "invested"
        ? investedExw(setup, list, currency, brand, base)
        : distributionExw(setup, list, currency, brand, base);
    if (amounts) done.set(comboKey(list.priceList, currency), { list, currency, ...amounts });
  }
  // the base price as Tomorrow calculated it: what a DDP list without an EXW list of its region starts from
  const baseResult = base ? done.get(comboKey(base.priceList, base.currency)) : undefined;

  return combos.map(({ list, currency }) => {
    const key = comboKey(list.priceList, currency);
    const brand = own.get(key);
    const channel = channelOf(list);
    if (channel === "exw") return present(setup, list, currency, done.get(key));
    if (channel === "agency") return agency(setup, list, currency, brand);
    if (channel === "ddp") {
      const exw = combos.find(
        (c) => c.list.channel === "exw" && c.list.region === exwRegionOf(list.region) && c.currency === currency,
      );
      const source = exw ? done.get(comboKey(exw.list.priceList, currency)) : undefined;
      const amounts =
        setup.brandType === "invested"
          ? investedDdp(setup, list, currency, source, base, baseResult)
          : distributionDdp(setup, list, currency, brand, source, baseResult);
      return present(setup, list, currency, amounts ? { list, currency, ...amounts } : undefined);
    }
    // no formula for the other channels (Farfetch) in the current portal: their prices are set by hand
    return present(setup, list, currency, undefined);
  });
}

const comboKey = (priceList: string, currency: string) => `${priceList}/${currency}`;

/** A price the brand entered: zero counts as not entered, as in the current portal. */
function entered(value: string | null | undefined): Big | null {
  if (value === null || value === undefined) return null;
  const amount = new Big(value);
  return amount.gt(0) ? amount : null;
}

/** A percentage as a factor: 12.5 → 0.125; a percentage not set is zero. */
const pct = (value: string | null) => new Big(value ?? 0).div(100);
const plusPct = (...values: (string | null)[]) => values.reduce((sum, v) => sum.plus(pct(v)), new Big(1));
const currencyMargin = (list: PriceListSetup, currency: string) =>
  list.currencies.find((c) => c.currency === currency)?.extraMarginPct ?? null;
const ceilTo = (amount: Big, unit: number) => amount.div(unit).round(0, Big.roundUp).times(unit);
const isSample = (price: BrandPrice | undefined) =>
  price?.brandWholesalePrice != null && new Big(price.brandWholesalePrice).eq(SAMPLE_PRICE);

/** A distribution brand needs a wholesale or a purchase price, an invested one all three. */
function isBase(brandType: BrandType, p: BrandPrice): boolean {
  const purchase = entered(p.brandPurchasePrice);
  const wholesale = entered(p.brandWholesalePrice);
  return brandType === "invested"
    ? purchase !== null && wholesale !== null && entered(p.brandRetailPrice) !== null
    : purchase !== null || wholesale !== null;
}

/**
 * The rate from one currency to another: the one Finance entered, its inverse, or the two through the euro.
 * Null when Finance has no rate for the pair.
 */
function fxRate(rates: PricingSetup["fxRates"], from: string, to: string): Big | null {
  if (from === to) return new Big(1);
  const direct = (a: string, b: string): Big | null => {
    const rate = rates.find((r) => r.from === a && r.to === b);
    if (rate) return new Big(rate.rate);
    const inverse = rates.find((r) => r.from === b && r.to === a);
    return inverse ? new Big(1).div(inverse.rate) : null;
  };
  const rate = direct(from, to);
  if (rate) return rate;
  const toEuro = direct(from, "EUR");
  const fromEuro = direct("EUR", to);
  return toEuro && fromEuro ? toEuro.times(fromEuro) : null;
}

/**
 * The brand price an EXW list starts from, its own if it has what the brand type needs, otherwise the base one, and
 * the rate to the currency of the list. Null without a price to start from or without a rate.
 */
function exwSource(
  setup: PricingSetup,
  currency: string,
  brand: BrandPrice | undefined,
  base: BrandPrice | undefined,
): { source: BrandPrice; rate: Big } | null {
  const source = brand && isBase(setup.brandType, brand) ? brand : base;
  if (!source) return null;
  const rate = source === brand ? new Big(1) : fxRate(setup.fxRates, source.currency, currency);
  return rate && { source, rate };
}

/**
 * Distribution, EXW. Tomorrow buys at the brand's wholesale price less the distribution discount, or at the brand's
 * purchase price when there is no wholesale; it sells at cost plus inbound costs, over its margin.
 */
function distributionExw(
  setup: PricingSetup,
  list: PriceListSetup,
  currency: string,
  brand: BrandPrice | undefined,
  base: BrandPrice | undefined,
): Calculated | null {
  const from = exwSource(setup, currency, brand, base);
  if (!from) return null;
  const { source, rate } = from;
  const exw = exwFromBrand(list, source, currency);
  return {
    purchase: exw.purchase.times(rate),
    wholesale: exw.wholesale.times(rate),
    retail: exw.retail?.times(rate) ?? null,
    sample: isSample(source),
  };
}

function exwFromBrand(list: PriceListSetup, brand: BrandPrice, currency: string, { margins = true } = {}) {
  const brandWholesale = entered(brand.brandWholesalePrice);
  const purchase = brandWholesale
    ? brandWholesale.times(new Big(1).minus(pct(list.distributionDiscountPct)))
    : (entered(brand.brandPurchasePrice) ?? new Big(0));
  const wholesale = purchase
    .times(plusPct(list.freightInPct, list.dutiesInPct, list.warehousingPct))
    .div(new Big(1).minus(pct(list.tomorrowMarginPct)))
    .times(margins ? plusPct(list.extraMarginPct) : 1);
  const retail =
    list.retailMultiplier === null
      ? null
      : wholesale.times(list.retailMultiplier).times(margins ? plusPct(currencyMargin(list, currency)) : 1);
  return { purchase, wholesale, retail };
}

/**
 * Distribution, DDP: the EXW price of the same region and currency plus outbound costs. Without that EXW list, the
 * list's own brand price makes one with its own setup, and without a brand price the base price does, converted.
 */
function distributionDdp(
  setup: PricingSetup,
  list: PriceListSetup,
  currency: string,
  brand: BrandPrice | undefined,
  exw: Working | undefined,
  baseResult: Working | undefined,
): Calculated | null {
  let source: Calculated;
  if (exw) {
    source = roundedAmounts(setup, exw);
  } else if (brand && entered(brand.brandWholesalePrice)) {
    const own = exwFromBrand(list, brand, currency, { margins: false });
    source = { ...own, retail: own.retail?.round(0, Big.roundUp) ?? null, sample: false };
  } else {
    const rate = baseResult && fxRate(setup.fxRates, baseResult.currency, currency);
    if (!baseResult || !rate) return null;
    const rounded = roundedAmounts(setup, baseResult);
    source = {
      purchase: rounded.purchase?.times(rate) ?? null,
      wholesale: rounded.wholesale.times(rate),
      retail: rounded.retail?.times(rate) ?? null,
      sample: rounded.sample,
    };
  }
  return {
    purchase: source.purchase,
    wholesale: source.wholesale
      .times(plusPct(list.freightOutPct, list.dutiesOutPct))
      .times(plusPct(list.extraMarginPct)),
    retail: source.retail?.times(plusPct(currencyMargin(list, currency))) ?? null,
    sample: source.sample || isSample(brand),
  };
}

/**
 * Invested, EXW: no Tomorrow margin. The brand's wholesale price plus inbound costs, the brand's retail price plus
 * the currency margin; the purchase price stays the brand's, in its own currency.
 */
function investedExw(
  setup: PricingSetup,
  list: PriceListSetup,
  currency: string,
  brand: BrandPrice | undefined,
  base: BrandPrice | undefined,
): Calculated | null {
  const from = exwSource(setup, currency, brand, base);
  if (!from) return null;
  const { source, rate } = from;
  return {
    purchase: new Big(source.brandPurchasePrice ?? 0),
    wholesale: new Big(source.brandWholesalePrice ?? 0)
      .times(rate)
      .times(plusPct(list.freightInPct, list.dutiesInPct, list.warehousingPct))
      .times(plusPct(list.extraMarginPct)),
    retail: new Big(source.brandRetailPrice ?? 0).times(plusPct(currencyMargin(list, currency))).times(rate),
    sample: false,
  };
}

/** Invested, DDP: the EXW price plus outbound costs; without an EXW list, the base brand price with both costs. */
function investedDdp(
  setup: PricingSetup,
  list: PriceListSetup,
  currency: string,
  exw: Working | undefined,
  base: BrandPrice | undefined,
  baseResult: Working | undefined,
): Calculated | null {
  const outbound = plusPct(list.freightOutPct, list.dutiesOutPct).times(plusPct(list.extraMarginPct));
  if (exw) {
    const source = roundedAmounts(setup, exw);
    return {
      purchase: source.purchase,
      wholesale: source.wholesale.times(outbound),
      retail: source.retail?.times(plusPct(currencyMargin(list, currency))) ?? null,
      sample: false,
    };
  }
  const rate = baseResult && fxRate(setup.fxRates, baseResult.currency, currency);
  if (!base || !baseResult || !rate) return null;
  const source = roundedAmounts(setup, baseResult);
  return {
    purchase: source.purchase,
    wholesale: new Big(base.brandWholesalePrice ?? 0)
      .times(rate)
      .times(plusPct(list.freightInPct, list.dutiesInPct, list.warehousingPct))
      .times(outbound),
    retail: source.retail?.times(plusPct(currencyMargin(list, currency))).times(rate) ?? null,
    sample: false,
  };
}

/** Agency: the brand's prices, only rounded. */
function agency(
  setup: PricingSetup,
  list: PriceListSetup,
  currency: string,
  brand: BrandPrice | undefined,
): CalculatedPrice {
  const wholesale = entered(brand?.brandWholesalePrice);
  const retail = entered(brand?.brandRetailPrice);
  return {
    priceList: list.priceList,
    currency,
    purchasePrice: money(entered(brand?.brandPurchasePrice)),
    wholesalePrice: money(wholesale && setup.wholesaleRoundup ? wholesale.round(0, Big.roundUp) : wholesale),
    retailPrice: money(retail?.round(0, Big.roundUp) ?? null),
  };
}

/** The amounts as they are written: what the lists that start from this one read. */
function roundedAmounts(setup: PricingSetup, w: Working): Calculated {
  if (w.sample) return { purchase: new Big(SAMPLE_PRICE), wholesale: new Big(1), retail: new Big(1), sample: true };
  const retailUnit = setup.brandType === "invested" ? setup.investedRetailRoundup : 1;
  return {
    purchase: w.purchase,
    wholesale: setup.wholesaleRoundup ? w.wholesale.round(0, Big.roundUp) : w.wholesale,
    retail: w.retail ? ceilTo(w.retail.round(0, Big.roundUp), retailUnit) : null,
    sample: false,
  };
}

function present(
  setup: PricingSetup,
  list: PriceListSetup,
  currency: string,
  working: Working | undefined,
): CalculatedPrice {
  const amounts = working ? roundedAmounts(setup, working) : null;
  return {
    priceList: list.priceList,
    currency,
    purchasePrice: money(amounts?.purchase ?? null),
    wholesalePrice: money(amounts?.wholesale ?? null),
    retailPrice: money(amounts?.retail ?? null),
  };
}

const money = (amount: Big | null) => (amount === null ? null : amount.round(MONEY_SCALE, Big.roundHalfUp).toString());

// --- The intercompany price of a brand (B-69, ADR-249) ---

/** What a brand's intercompany rule finds for an SKU, as logistics reads it via SQL. */
export interface IntercompanyBase {
  method: IntercompanyPriceMethod;
  /** `po_cost_markup`: the percentage points added to the cost. */
  markupPct: string | null;
  /** `price_list`: the wholesale price of the SKU's style on the dedicated list, null if it has none. */
  listPrice: string | null;
  /** `po_cost_markup`: the unit cost of the SKU's last purchase order, null if it was never bought. */
  poCost: string | null;
}

/**
 * The value of one unit of an SKU moved to another company of the group (ADR-249): the price of the dedicated list as
 * it is, or the cost of the last purchase order raised by the brand's markup and rounded to the cent. Null when the
 * rule finds nothing for the SKU: the transfer does not leave without a price.
 */
export function intercompanyUnitPrice(base: IntercompanyBase): string | null {
  if (base.method === "price_list") return base.listPrice;
  if (base.poCost === null) return null;
  const factor = new Big(1).plus(new Big(base.markupPct ?? 0).div(100));
  return new Big(base.poCost).times(factor).round(2, Big.roundHalfUp).toFixed(2);
}

/**
 * Where an attribute task stands for who follows it (ADR-309): the status of its SKUs while its attributes are a
 * draft, else the status of its attributes (submitted, in sync, approved).
 */
export const attributeTaskStage = (task: {
  status: AttributeTaskStatus;
  attributesStatus: AttributesStatus;
}): AttributeTaskStage => (task.attributesStatus === "draft" ? task.status : task.attributesStatus);
